import React, { useMemo } from 'react'
import classNames from 'classnames'
import { colors } from '@/constants/colors'
import type { NamedColor } from '@/constants/types'

type FontFace = 'angelSans' | 'roboto' | 'dharma-gothic-e' | 'bio-sans'
export type FontWeight = 'normal' | 'medium' | 'semibold' | 'bold'

export type PhotonTextClassNames =
  | 'photon-display'
  | 'photon-heading-xl'
  | 'photon-heading-lg'
  | 'photon-heading-md'
  | 'photon-heading-sm'
  | 'photon-heading-xs'
  | 'photon-title-lg'
  | 'photon-title-md'
  | 'photon-title-sm'
  | 'photon-title-xs'
  | 'photon-title-xxs'
  | 'photon-eyebrow-lg'
  | 'photon-eyebrow-md'
  | 'photon-eyebrow-sm'
  | 'photon-eyebrow-xs'
  | 'photon-paragraph-xl'
  | 'photon-paragraph-lg'
  | 'photon-paragraph-md'
  | 'photon-paragraph-sm'
  | 'photon-caption-lg'
  | 'photon-caption-md'
  | 'photon-caption-sm'
  | 'photon-caption-xs'
  | 'photon-label-lg'
  | 'photon-label-md'
  | 'photon-label-sm'
  | 'photon-label-xs'
  | 'photon-footer'

interface RenderPropProps {
  className: string
  style: React.CSSProperties
}

type RenderProp = (props: RenderPropProps) => JSX.Element

export interface TextProps extends React.HTMLAttributes<unknown> {
  as?: RenderProp | React.ElementType
  className?: string
  color?: NamedColor
  face?: FontFace
  weight?: FontWeight
  italic?: boolean
  strikethrough?: boolean
  underline?: boolean
}

/** Type guard to determine if the `as` prop is a render prop function. */
function isRenderProp(as: RenderProp | React.ElementType): as is RenderProp {
  return typeof as === 'function' && 'length' in as && as.length === 1
}

export const Text = React.forwardRef<HTMLElement, TextProps>(
  ({ face = 'angelSans', weight = 'medium', italic, underline, strikethrough, color, className, as, ...rest }, ref) => {
    /*
  The color prop is set using style because we want the text component to use by
  default whatever text class has been applied to a parent container. We should
  only provide a custom text color if it's provided directly through the props, and
  using the style instead of a class will ensure that this color always is given priority.
  */
    const style = useMemo(() => {
      return color ? { color: colors[color] } : {}
    }, [color])

    const computedClassName = classNames(className, FONT_FACE[face], FONT_WEIGHT[weight], {
      italic: italic,
      underline: underline,
      'line-through': strikethrough,
      'text-inherit': !color,
    })

    const pprops = {
      className: computedClassName,
      style,
      ...rest,
      ref,
    }

    // Default to span
    if (!as) {
      return <span {...pprops} />
    }

    // Support legacy render prop pattern
    if (isRenderProp(as)) {
      const renderPropProps: RenderPropProps = {
        className: computedClassName,
        style,
        ...rest,
      }
      return as(renderPropProps)
    }

    // TypeScript now knows `as` is React.ElementType — no assertion needed
    const Component = as
    return <Component {...pprops} />
  },
)

Text.displayName = 'Text'

const FONT_FACE = {
  angelSans: 'font-angelSans',
  roboto: 'font-roboto',
  dharmaGothicE: 'dharma-gothic-e',
  bioSans: 'bio-sans',
} as unknown as Record<FontFace, string>

const FONT_WEIGHT = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
} as Record<FontWeight, string>

const TextHoc = (baseClass: PhotonTextClassNames | 'hidden', defaultAs: RenderProp | React.ElementType) => {
  const Component = React.forwardRef<HTMLElement, TextProps>(function Typography({ as, className, ...props }, ref) {
    return <Text ref={ref} as={as ?? defaultAs} className={classNames(baseClass, className)} {...props}></Text>
  })
  Component.displayName = `TextHoc(${baseClass})`
  return Component
}

// Legacy render prop helpers - kept for backward compatibility
export const AsH1 = (props: RenderPropProps) => <h1 {...props} />
export const AsH2 = (props: RenderPropProps) => <h2 {...props} />
export const AsH3 = (props: RenderPropProps) => <h3 {...props} />
export const AsH4 = (props: RenderPropProps) => <h4 {...props} />
export const AsH5 = (props: RenderPropProps) => <h5 {...props} />
export const AsH6 = (props: RenderPropProps) => <h6 {...props} />
export const AsLabel = (props: RenderPropProps) => <label {...props} />
export const AsFigCaption = (props: RenderPropProps) => <figcaption {...props} />
export const AsQ = (props: RenderPropProps) => <q {...props} />
export const AsP = (props: RenderPropProps) => <p {...props} />
export const AsSpan = (props: RenderPropProps) => <span {...props} />
export const AsDiv = (props: RenderPropProps) => <div {...props} />
export const AsSub = (props: RenderPropProps) => <sub {...props} />

export const Display = TextHoc('photon-display', 'h1')
/**
 * Using a HeadingInvisible in a section that otherwise would not have a Heading
 * aids screen readers and bots to better understand the content. Use the "as" param to
 * pass the proper heading element for the position in the document outline, defaults to h2.
 */
export const HeadingInvisible = TextHoc('hidden', 'h2')
export const HeadingXL = TextHoc('photon-heading-xl', 'h2')
export const HeadingLG = TextHoc('photon-heading-lg', 'h3')
export const HeadingMD = TextHoc('photon-heading-md', 'h4')
export const HeadingSM = TextHoc('photon-heading-sm', 'h5')
export const HeadingXS = TextHoc('photon-heading-xs', 'h6')

export const TitleLG = TextHoc('photon-title-lg', 'h2')
export const TitleMD = TextHoc('photon-title-md', 'h4')
export const TitleSM = TextHoc('photon-title-sm', 'h5')
export const TitleXS = TextHoc('photon-title-xs', 'h6')
export const TitleXXS = TextHoc('photon-title-xxs', 'h6')

export const EyebrowLG = TextHoc('photon-eyebrow-lg', 'label')
export const EyebrowMD = TextHoc('photon-eyebrow-md', 'label')
export const EyebrowSM = TextHoc('photon-eyebrow-sm', 'label')
export const EyebrowXS = TextHoc('photon-eyebrow-xs', 'label')

export const ParagraphXL = TextHoc('photon-paragraph-xl', 'p')
export const ParagraphLG = TextHoc('photon-paragraph-lg', 'p')
export const ParagraphMD = TextHoc('photon-paragraph-md', 'p')
export const ParagraphSM = TextHoc('photon-paragraph-sm', 'p')

export const CaptionLG = TextHoc('photon-caption-lg', 'p')
export const CaptionMD = TextHoc('photon-caption-md', 'p')
export const CaptionSM = TextHoc('photon-caption-sm', 'p')
export const CaptionXS = TextHoc('photon-caption-xs', 'p')

export const LabelLG = TextHoc('photon-label-lg', 'p')
export const LabelMD = TextHoc('photon-label-md', 'p')
export const LabelSM = TextHoc('photon-label-sm', 'p')
export const LabelXS = TextHoc('photon-label-xs', 'p')

export const Footer = TextHoc('photon-footer', 'span')
